import React, { useState } from "react";
import { message, Upload } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";

import {
  useDeleteCompanyMutation,
  useGetListCompanyQuery,
  useImportCompanyEmployeeCSVMutation,
} from "../../store/Company";
import ButtonBase from "../../components/Button/index";
import TableBaseComponent from "../../components/FormComponent/Table";
import HeaderContent from "../../components/HeaderContent";
import PopConfirmBase from "../../components/PopupConfirm";
import { handlePageChange, handlePageSizeChange } from "../../constants/ultil";
import withPermissionCheck from "../../constants/PermissionHook";
import { useGetListCompanyHierarchyQuery } from "../../store/Settings/CompanyHierarchy";
import { useGetListPositionsQuery } from "../../store/Settings/Positions";
import { useGetListDepartmentsQuery } from "../../store/Settings/Departments";
import { useGetListJobTitleQuery } from "../../store/Settings/JobTitle";
import { useGetListEmploymentTypeQuery } from "../../store/Settings/EmploymentType";

const REQUIRED_FIELDS_CSV = [
  "企業ID",
  "氏名",
  "生年月日",
  "階層",
  "役職",
  "部署",
  "職種",
  "雇用形態",
];
const FIELDS_CSV = [
  "企業ID",
  "社員ID",
  "氏名",
  "生年月日",
  "階層",
  "役職",
  "部署",
  "職種",
  "雇用形態",
];
const MAPPING_KEY_CSV = [
  "company_id",
  "id",
  "name",
  "date_of_birth",
  "hierarchy_id",
  "position_id",
  "department_id",
  "job_title_id",
  "employment_type_id",
];
const MASTER_DATA_KEY = [
  "hierarchy_id",
  "position_id",
  "department_id",
  "job_title_id",
  "employment_type_id",
];

const ListCompanyContainer = ({
  hasCreatePermission,
  hasDetailPermission,
  hasDeletePermission,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteCompany] = useDeleteCompanyMutation();
  const [importCSV] = useImportCompanyEmployeeCSVMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProcessed, setFileProcessed] = useState(null);
  const [params, setParams] = useState({
    limit: 10,
    page: 1,
  });

  const { data: listCompany, isFetching } = useGetListCompanyQuery(params, {
    refetchOnMountOrArgChange: true, // auto call API when component mount
  });
  const { data: listHierarchy } = useGetListCompanyHierarchyQuery(
    { ...params, limit: 100 },
    {
      refetchOnMountOrArgChange: true, // auto call API when component mount
    }
  );
  const { data: listPosition } = useGetListPositionsQuery(
    { ...params, limit: 100 },
    {
      refetchOnMountOrArgChange: true, // auto call API when component mount
    }
  );
  const { data: listDepartment } = useGetListDepartmentsQuery(
    { ...params, limit: 100 },
    {
      refetchOnMountOrArgChange: true, // auto call API when component mount
    }
  );
  const { data: listJobType } = useGetListJobTitleQuery(
    { ...params, limit: 100 },
    {
      refetchOnMountOrArgChange: true, // auto call API when component mount
    }
  );
  const { data: listEmploymentType } = useGetListEmploymentTypeQuery(
    { ...params, limit: 100 },
    {
      refetchOnMountOrArgChange: true, // auto call API when component mount
    }
  );

  const getIdAndName = (data) => {
    return data?.map((item) => ({
      id: item?.id,
      name: item?.name,
    }));
  };

  const masterData = {
    hierarchy_id: getIdAndName(listHierarchy?.data?.data),
    position_id: getIdAndName(listPosition?.data?.data),
    department_id: getIdAndName(listDepartment?.data?.data),
    job_title_id: getIdAndName(listJobType?.data?.data),
    employment_type_id: getIdAndName(listEmploymentType?.data?.data),
  };

  console.log("masterData", masterData);

  const beforeUpload = (file, info) => {
    const isCSV = file.type === "text/csv";
    if (!isCSV) {
      message.error("CSVファイルをアップロードしてください！");

      info.length = 0;
      return false;
    }

    return true;
  };

  function objectToArray(obj) {
    if (!obj || typeof obj !== "object") {
      return [];
    }

    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  function getDataRowCSV(data) {
    if (!Array.isArray(data) || data.length < 2) return [];

    return data.map((obj) =>
      Object.entries(obj).map(([key, value]) => ({ key, value }))
    );
  }

  function checkRowHaveData(data, data2, value) {
    const index = data.findIndex((item) => item.key === value);
    if (index === -1) return false;

    return data2.every((row) => row[index]?.value?.trim() !== "");
  }

  function transformCSVData(data) {
    let rowUndefined = [];
    const transformData = data.map((row, idxRow) => {
      let newObj = {};
      row.forEach((item, index) => {
        const key = MAPPING_KEY_CSV[index];
        if (key) {
          if (MASTER_DATA_KEY?.includes(key)) {
            const dataKey = masterData?.[key]?.find(
              (i) => i?.name === item?.value
            )?.id;
            if (!dataKey) {
              rowUndefined.push(
                `${Number(idxRow) + 1}行目に記入した「${
                  FIELDS_CSV[index]
                }」は、有効ではありません。`
              );
            }
            newObj[key] = dataKey;
          } else {
            newObj[key] = item.value;
          }
        }
      });

      return newObj;
    });

    return { data: transformData, error: rowUndefined };
  }

  function filterArraysWithNonEmptyValues(data) {
    return data
      .filter((subArray) => {
        return subArray.some((item) => item.value !== "");
      })
      ?.map((item) => item?.slice(0, 9));
  }

  const onChange = async (info) => {
    const file = info?.fileList?.[info?.fileList?.length - 1];

    if (!file?.originFileObj || fileProcessed === file.uid) {
      return;
    }

    if (!file?.originFileObj) {
      message.error("ファイルを選択してください。");
      setIsLoading(false);
      return;
    }

    let hasMissingField = [];

    if (file && file?.originFileObj instanceof Blob) {
      setFileProcessed(file.uid);

      const fileReaderCSV = new FileReader();

      fileReaderCSV.onload = () => {
        const result = fileReaderCSV.result;
        if (!result) {
          message.error("ファイルを読み取れませんでした。");
          setIsLoading(false);
          return;
        }

        Papa.parse(result, {
          header: true,
          complete: async (parseResult) => {
            const dataLabel = objectToArray(parseResult?.data?.[0]);
            const dataRow = filterArraysWithNonEmptyValues(
              getDataRowCSV(parseResult?.data)
            );

            if (dataLabel?.length) {
              hasMissingField = REQUIRED_FIELDS_CSV.filter(
                (field) => !checkRowHaveData(dataLabel, dataRow, field)
              );
            }

            if (hasMissingField.length > 0) {
              setIsLoading(false);

              const missingFieldsMessage = hasMissingField.map(
                (item) => `「${item}」`
              );
              message.error(
                `${missingFieldsMessage}の情報を入力してください。`
              );

              return;
            }

            const dataImport = transformCSVData(dataRow);
            if (dataImport?.error?.length > 0) {
              message.error({
                content: (
                  <div>
                    {dataImport?.error.slice(0, 5).map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </div>
                ),
                duration: 5,
              });
              return;
            }

            setIsLoading(true);

            const res = await importCSV(dataImport?.data);

            if (res?.data?.success) {
              message.success(`正常にアップロード。`);
              setIsLoading(false);
            } else {
              const fieldMap = MAPPING_KEY_CSV.reduce((acc, key, index) => {
                acc[key] = FIELDS_CSV[index] || key;
                return acc;
              }, {});

              const formattedErrors = Object.entries(
                res?.error?.data?.data
              ).map(([key, messages]) => {
                const [row, field] = key.split(".");
                const fieldText = fieldMap[field] || field;
                return `${
                  Number(row) + 1
                }行目に記入した「${fieldText}」は、有効ではありません。`;
              });

              setIsLoading(false);
              if (formattedErrors?.length > 0) {
                message.error({
                  content: (
                    <div>
                      {formattedErrors.slice(0, 5).map((error, index) => (
                        <div key={index}>{error}</div>
                      ))}
                    </div>
                  ),
                  duration: 5,
                });
              } else {
                message.error(
                  "ファイルのアップロード中にエラーが発生しました。管理者に連絡してください。"
                );
              }
            }
          },
          skipEmptyLines: true,
        });
      };

      fileReaderCSV.onerror = () => {
        message.error(`ファイルの読み込みに失敗しました。`);

        setIsLoading(false);
      };

      fileReaderCSV.readAsText(file.originFileObj);
    } else {
      message.error(`ファイルを選択してください。`);
      setIsLoading(false);
    }
  };

  const onDeleteCompany = async (record) => {
    try {
      await deleteCompany(record.id).unwrap();
      message.success(t("setting.deleteSuccess"));
    } catch (error) {
      message.error(error?.data?.message);
    }
  };

  const columns = [
    {
      title: "ID",
      key: "id",
      dataIndex: "id",
      width: 100,
    },
    {
      title: t("company.companyName"),
      key: "name",
      dataIndex: "name",
    },
    {
      title: t("company.address"),
      key: "address",
      dataIndex: "address",
    },
    {
      title: t("common.industry"),
      key: "industry",
      dataIndex: "industry",
      render: (_, record) => {
        return record?.industry?.name;
      },
    },
    {
      title: t("company.numberOfEmployee"),
      key: "number_of_employee",
      dataIndex: "number_of_employee",
      render: (_, record) => {
        return record?.number_of_employee;
      },
    },
    {
      title: t("setting.operation"),
      key: "action",
      render: (_, record) => (
        <div className="flex text-[var(--primary-color)] cursor-pointer font-medium">
          <div
            className={`mr-5 text-[var(--primary-color)] ${
              hasDetailPermission ? "" : "!text-[#a9a9a9] cursor-not-allowed"
            }`}
            onClick={() => {
              if (hasDetailPermission) {
                navigate(`/list-company/edit/${record.id}`);
              }
            }}
          >
            {t("setting.edit")}
          </div>
          {hasDeletePermission ? (
            <PopConfirmBase
              title={t("common.confirmDelete")}
              onConfirm={() => onDeleteCompany(record)}
              okButtonProps={{ loading: false }}
            >
              <div className="font-medium text-[var(--primary-color)] cursor-pointer">
                {t("setting.delete")}
              </div>
            </PopConfirmBase>
          ) : (
            <div className="font-medium text-[#a9a9a9] cursor-not-allowed">
              {t("setting.delete")}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between bg-white border-b border-[#e4e4e4]">
        <HeaderContent
          textCurrentPage={t("common.listCompany")}
          className="ml-3"
        />
        <div className="flex items-center pr-5">
          <Upload
            onChange={onChange}
            beforeUpload={beforeUpload}
            accept=".csv"
            showUploadList={false}
          >
            <ButtonBase
              className="w-[108px] h-[36px] !bg-white !text-[#0B0A0A]"
              title={t("company.importCSV")}
              loading={isLoading}
            />
          </Upload>
          <ButtonBase
            className="w-[108px] h-[36px] ml-[10px]"
            title={t("account.newRegistration")}
            onClick={() => navigate("/list-company/create")}
            disabled={!hasCreatePermission}
          />
        </div>
      </div>
      <div className="p-4">
        <TableBaseComponent
          loading={isFetching}
          columns={columns}
          dataSource={listCompany?.data?.data}
          paginationOptions={{
            total: listCompany?.data?.total,
            defaultPageSize: params.limit,
            current: params?.page,
          }}
          handlePageChange={(pageChange) =>
            handlePageChange(pageChange, setParams)
          }
          handlePageSizeChange={(current, size) =>
            handlePageSizeChange(size, setParams)
          }
        />
      </div>
    </div>
  );
};

export default withPermissionCheck(ListCompanyContainer, {
  view: "list-companies",
  create: "create-company",
  detail: "detail-company",
  edit: "update-company",
  delete: "delete-company",
});
